import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
    ChevronLeft, Save, Loader2, Plus, Trash2, GripVertical, ChevronUp, ChevronDown,
    Settings2, Palette, Eye, Code, Copy, X, Tag
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_TYPES = [
    { value: 'text', label: 'Short Text' },
    { value: 'textarea', label: 'Long Text' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'url', label: 'URL' },
    { value: 'select', label: 'Dropdown' },
    { value: 'multi_select', label: 'Multi Select' },
    { value: 'checkbox', label: 'Checkbox' },
];

// v2.16.39 BUG-054: form fields can now map to COMPANY fields too, not
// just contact fields. Backend handles the account upsert when
// company_name is present (see backend/routes/forms.py submit_form).
const CONTACT_MAPPINGS = [
    { value: '', label: 'No mapping' },
    { group: 'Contact', value: 'first_name', label: 'First Name' },
    { group: 'Contact', value: 'last_name', label: 'Last Name' },
    { group: 'Contact', value: 'email', label: 'Email' },
    { group: 'Contact', value: 'phone', label: 'Phone' },
    { group: 'Contact', value: 'mobile', label: 'Mobile' },
    { group: 'Contact', value: 'title', label: 'Job Title' },
    { group: 'Contact', value: 'department', label: 'Department' },
    { group: 'Contact', value: 'seniority', label: 'Seniority' },
    { group: 'Contact', value: 'linkedin_url', label: 'LinkedIn URL' },
    { group: 'Company', value: 'company_name', label: 'Company Name' },
    { group: 'Company', value: 'company_website', label: 'Company Website' },
    { group: 'Company', value: 'industry', label: 'Industry' },
    { group: 'Company', value: 'company_size', label: 'Company Size' },
    { group: 'Company', value: 'company_phone', label: 'Company Phone' },
    { group: 'Company', value: 'company_email', label: 'Company Email' },
    { group: 'Company', value: 'company_city', label: 'Company City' },
    { group: 'Company', value: 'company_country', label: 'Company Country' },
    { group: 'Company', value: 'company_linkedin_url', label: 'Company LinkedIn URL' },
];

const INPUT = "w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20";
const LABEL = "block text-xs font-medium text-stone-600 mb-1";

function genId() { return 'fld_' + Math.random().toString(36).substr(2, 9); }

function FieldCard({ field, index, total, onUpdate, onRemove, onMoveUp, onMoveDown }) {
    const [expanded, setExpanded] = useState(false);
    return (
        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 flex items-center gap-2 bg-stone-50">
                <GripVertical className="w-3.5 h-3.5 text-stone-300 flex-shrink-0" />
                <input value={field.label} onChange={e => onUpdate({ label: e.target.value })}
                    className="flex-1 text-sm font-medium text-stone-800 bg-transparent border-none outline-none"
                    placeholder="Field label..." />
                <select value={field.field_type} onChange={e => onUpdate({ field_type: e.target.value })}
                    className="text-xs border border-stone-200 rounded px-1.5 py-1 bg-white">
                    {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <label className="flex items-center gap-1 text-xs text-stone-500">
                    <input type="checkbox" checked={field.required || false} onChange={e => onUpdate({ required: e.target.checked })}
                        className="rounded border-stone-300" />
                    Req
                </label>
                <button onClick={() => setExpanded(!expanded)} className="p-1 text-stone-400 hover:text-stone-600">
                    {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                <button onClick={onRemove} className="p-1 text-stone-400 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            </div>
            {expanded && (
                <div className="px-3 py-2 space-y-2 border-t border-stone-100">
                    <div>
                        <label className={LABEL}>Placeholder</label>
                        <input value={field.placeholder || ''} onChange={e => onUpdate({ placeholder: e.target.value })}
                            className={INPUT} placeholder="Placeholder text..." />
                    </div>
                    <div>
                        <label className={LABEL}>Map to CRM Field</label>
                        {/* v2.16.39 BUG-054: grouped by Contact / Company */}
                        <select value={field.map_to || ''} onChange={e => onUpdate({ map_to: e.target.value || null })} className={INPUT}>
                            <option value="">No mapping</option>
                            <optgroup label="Contact Fields">
                                {CONTACT_MAPPINGS.filter(m => m.group === 'Contact').map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </optgroup>
                            <optgroup label="Company Fields">
                                {CONTACT_MAPPINGS.filter(m => m.group === 'Company').map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </optgroup>
                        </select>
                        {field.map_to && CONTACT_MAPPINGS.find(m => m.value === field.map_to)?.group === 'Company' && (
                            <p className="text-[10px] text-stone-400 mt-1">
                                Company mappings upsert an account on submission. Requires a <code className="bg-stone-100 px-1 rounded">Company Name</code> field too so the account can be identified.
                            </p>
                        )}
                    </div>
                    {(field.field_type === 'select' || field.field_type === 'multi_select') && (
                        <div>
                            <label className={LABEL}>Options (one per line)</label>
                            <textarea value={(field.options || []).join('\n')}
                                onChange={e => onUpdate({ options: e.target.value.split('\n').filter(Boolean) })}
                                rows={3} className={INPUT} placeholder="Option 1&#10;Option 2&#10;Option 3" />
                        </div>
                    )}
                    <div className="flex gap-1">
                        <button disabled={index === 0} onClick={onMoveUp}
                            className="text-xs text-stone-400 hover:text-stone-700 disabled:opacity-30 px-2 py-1">↑ Move Up</button>
                        <button disabled={index === total - 1} onClick={onMoveDown}
                            className="text-xs text-stone-400 hover:text-stone-700 disabled:opacity-30 px-2 py-1">↓ Move Down</button>
                    </div>
                </div>
            )}
        </div>
    );
}

function TagInput({ value = [], onChange, placeholder = 'Type and press Enter...' }) {
    const [input, setInput] = useState('');
    const add = () => {
        const v = input.trim();
        if (v && !value.includes(v)) { onChange([...value, v]); }
        setInput('');
    };
    return (
        <div>
            <div className="flex flex-wrap gap-1 mb-1">
                {value.map((t, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-stone-100 text-stone-700 text-xs px-2 py-0.5 rounded-full">
                        {t}
                        <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-stone-400 hover:text-red-500">
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
            </div>
            <input value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                placeholder={placeholder} className={INPUT} />
        </div>
    );
}

export default function FormBuilder() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(!!formId);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('settings');
    const isNew = !formId || formId === 'new';

    useEffect(() => {
        if (isNew) {
            setForm({
                name: 'Untitled Form', description: '',
                fields: [
                    { field_id: genId(), label: 'First Name', field_type: 'text', required: true, map_to: 'first_name', order: 0 },
                    { field_id: genId(), label: 'Last Name', field_type: 'text', required: false, map_to: 'last_name', order: 1 },
                    { field_id: genId(), label: 'Email', field_type: 'email', required: true, map_to: 'email', order: 2 },
                ],
                settings: {
                    confirmation_message: 'Thank you for your submission!',
                    auto_tags: [], notify_emails: [], create_contact_if_missing: true,
                },
                styling: { brand_color: '#1c1917', button_text: 'Submit', show_branding: true },
            });
            setLoading(false);
            return;
        }
        axios.get(`${API}/forms/${formId}`, { headers: getAuthHeader() })
            .then(res => setForm(res.data))
            .catch(() => { toast.error('Form not found'); navigate('/forms'); })
            .finally(() => setLoading(false));
    }, [formId, isNew, getAuthHeader, navigate]);

    const handleSave = async () => {
        if (!form?.name?.trim()) { toast.error('Form name required'); return; }
        setSaving(true);
        try {
            if (isNew) {
                const res = await axios.post(`${API}/forms`, form, { headers: getAuthHeader() });
                toast.success('Form created');
                navigate(`/forms/${res.data.form_id}/edit`, { replace: true });
                setForm(res.data);
            } else {
                const res = await axios.put(`${API}/forms/${formId}`, form, { headers: getAuthHeader() });
                toast.success('Form saved');
                setForm(res.data);
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
        finally { setSaving(false); }
    };

    const updateField = (index, updates) => {
        const fields = [...form.fields];
        fields[index] = { ...fields[index], ...updates };
        setForm({ ...form, fields });
    };

    const removeField = (index) => {
        setForm({ ...form, fields: form.fields.filter((_, i) => i !== index) });
    };

    const moveField = (index, dir) => {
        const fields = [...form.fields];
        const target = index + dir;
        if (target < 0 || target >= fields.length) return;
        [fields[index], fields[target]] = [fields[target], fields[index]];
        fields.forEach((f, i) => f.order = i);
        setForm({ ...form, fields });
    };

    const addField = (type = 'text') => {
        const newField = {
            field_id: genId(), label: '', field_type: type, required: false,
            placeholder: '', options: [], map_to: null, order: form.fields.length,
        };
        setForm({ ...form, fields: [...form.fields, newField] });
    };

    const updateSettings = (key, value) => setForm({ ...form, settings: { ...form.settings, [key]: value } });
    const updateStyling = (key, value) => setForm({ ...form, styling: { ...form.styling, [key]: value } });

    // Auto-detect email field
    const emailFields = (form?.fields || []).filter(f => f.field_type === 'email' || f.map_to === 'email');
    const emailFieldId = form?.settings?.email_field_id || emailFields[0]?.field_id || '';

    if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>;
    if (!form) return null;

    const TABS = [
        { key: 'settings', label: 'Settings', icon: Settings2 },
        { key: 'styling', label: 'Styling', icon: Palette },
        { key: 'preview', label: 'Preview', icon: Eye },
        { key: 'embed', label: 'Embed', icon: Code },
    ];

    return (
        <div className="h-screen flex flex-col bg-stone-50">
            {/* Top bar */}
            <div className="bg-white border-b border-stone-200 px-4 py-2.5 flex items-center justify-between flex-shrink-0 z-10">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/forms')} className="p-1.5 text-stone-400 hover:text-stone-700">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div className="flex flex-col">
                        <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                            className="text-sm font-semibold text-stone-900 bg-transparent border-none outline-none w-96"
                            placeholder="Internal form name..." />
                        <span className="text-[10px] text-stone-400">Internal name</span>
                    </div>
                </div>
                <Button size="sm" onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                    Save
                </Button>
            </div>

            {/* Main content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Left: Fields */}
                <div className="w-[500px] border-r border-stone-200 bg-white overflow-y-auto p-4 space-y-2">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-sm font-semibold text-stone-700">Form Fields</h2>
                        <span className="text-xs text-stone-400">{form.fields.length} fields</span>
                    </div>

                    {form.fields.map((field, i) => (
                        <FieldCard key={field.field_id} field={field} index={i} total={form.fields.length}
                            onUpdate={updates => updateField(i, updates)}
                            onRemove={() => removeField(i)}
                            onMoveUp={() => moveField(i, -1)}
                            onMoveDown={() => moveField(i, 1)} />
                    ))}

                    {/* Add field */}
                    <div className="pt-2 border-t border-stone-100">
                        <p className="text-[10px] text-stone-400 uppercase tracking-wider mb-1.5 font-semibold">Add Field</p>
                        <div className="flex flex-wrap gap-1">
                            {FIELD_TYPES.map(t => (
                                <button key={t.value} onClick={() => addField(t.value)}
                                    className="px-2 py-1 text-[11px] rounded bg-stone-50 text-stone-600 hover:bg-stone-100 border border-stone-200 transition-colors">
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right: Tabs */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Tab bar */}
                    <div className="bg-white border-b border-stone-200 px-4 flex gap-1">
                        {TABS.map(tab => {
                            const Icon = tab.icon;
                            return (
                                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                                    className={`px-3 py-2.5 text-xs font-medium flex items-center gap-1.5 border-b-2 transition-colors ${
                                        activeTab === tab.key ? 'border-stone-900 text-stone-900' : 'border-transparent text-stone-400 hover:text-stone-600'
                                    }`}>
                                    <Icon className="w-3.5 h-3.5" /> {tab.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Tab content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {activeTab === 'settings' && (
                            <div className="max-w-md space-y-4">
                                <div>
                                    <label className={LABEL}>Public Form Title</label>
                                    <input value={form.form_title || ''} onChange={e => setForm({ ...form, form_title: e.target.value })}
                                        className={INPUT} placeholder="Title shown to the public (defaults to form name if empty)" />
                                </div>
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={form.show_form_title !== false}
                                        onChange={e => setForm({ ...form, show_form_title: e.target.checked })}
                                        className="rounded border-stone-300" />
                                    Show title on public form
                                </label>
                                <div>
                                    <label className={LABEL}>Description</label>
                                    <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })}
                                        rows={2} className={INPUT} placeholder="Optional form description..." />
                                </div>
                                <div>
                                    <label className={LABEL}>Confirmation Message</label>
                                    <textarea value={form.settings?.confirmation_message || ''}
                                        onChange={e => updateSettings('confirmation_message', e.target.value)}
                                        rows={2} className={INPUT} />
                                </div>
                                <div>
                                    <label className={LABEL}>Redirect URL (optional)</label>
                                    <input value={form.settings?.redirect_url || ''} onChange={e => updateSettings('redirect_url', e.target.value)}
                                        className={INPUT} placeholder="https://yoursite.com/thank-you" />
                                    {form.settings?.redirect_url && (
                                        <label className="flex items-center gap-2 mt-2 cursor-pointer">
                                            <input type="checkbox"
                                                checked={form.settings?.redirect_new_tab !== false}
                                                onChange={e => updateSettings('redirect_new_tab', e.target.checked)}
                                                className="rounded border-stone-300 text-stone-900 focus:ring-stone-500" />
                                            <span className="text-xs text-stone-600">Open in a new tab</span>
                                        </label>
                                    )}
                                </div>
                                <div>
                                    <label className={LABEL}>Email Field (for contact matching)</label>
                                    <select value={emailFieldId} onChange={e => updateSettings('email_field_id', e.target.value)} className={INPUT}>
                                        <option value="">Auto-detect</option>
                                        {form.fields.filter(f => f.field_type === 'email' || f.map_to === 'email').map(f => (
                                            <option key={f.field_id} value={f.field_id}>{f.label || 'Untitled'}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className={LABEL}>Auto-apply Tags on Submission</label>
                                    <TagInput value={form.settings?.auto_tags || []}
                                        onChange={v => updateSettings('auto_tags', v)} placeholder="e.g. webinar_registered" />
                                </div>
                                <div>
                                    <label className={LABEL}>Notify Emails on Submission</label>
                                    <TagInput value={form.settings?.notify_emails || []}
                                        onChange={v => updateSettings('notify_emails', v)} placeholder="admin@company.com" />
                                </div>
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={form.settings?.create_contact_if_missing !== false}
                                        onChange={e => updateSettings('create_contact_if_missing', e.target.checked)}
                                        className="rounded border-stone-300" />
                                    Create contact if email not found
                                </label>
                            </div>
                        )}

                        {activeTab === 'styling' && (
                            <div className="max-w-md space-y-4">
                                <div>
                                    <label className={LABEL}>Brand Color</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" value={form.styling?.brand_color || '#1c1917'}
                                            onChange={e => updateStyling('brand_color', e.target.value)}
                                            className="w-10 h-10 rounded border border-stone-200 cursor-pointer" />
                                        <input value={form.styling?.brand_color || '#1c1917'}
                                            onChange={e => updateStyling('brand_color', e.target.value)}
                                            className={INPUT} />
                                    </div>
                                </div>
                                <div>
                                    <label className={LABEL}>Button Text</label>
                                    <input value={form.styling?.button_text || 'Submit'}
                                        onChange={e => updateStyling('button_text', e.target.value)} className={INPUT} />
                                </div>
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={form.styling?.show_branding !== false}
                                        onChange={e => updateStyling('show_branding', e.target.checked)}
                                        className="rounded border-stone-300" />
                                    Show "Powered by NurtureCRM" branding
                                </label>
                            </div>
                        )}

                        {activeTab === 'preview' && (
                            <div className="max-w-lg mx-auto bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
                                <h2 className="text-lg font-semibold text-stone-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{form.name}</h2>
                                {form.description && <p className="text-sm text-stone-500 mb-4">{form.description}</p>}
                                <div className="space-y-3">
                                    {form.fields.map(field => (
                                        <div key={field.field_id}>
                                            <label className="block text-sm font-medium text-stone-700 mb-1">
                                                {field.label || 'Untitled'} {field.required && <span className="text-red-500">*</span>}
                                            </label>
                                            {field.field_type === 'textarea' ? (
                                                <textarea disabled rows={3} className={INPUT} placeholder={field.placeholder} />
                                            ) : field.field_type === 'select' ? (
                                                <select disabled className={INPUT}>
                                                    <option>{field.placeholder || 'Select...'}</option>
                                                    {(field.options || []).map(o => <option key={o}>{o}</option>)}
                                                </select>
                                            ) : field.field_type === 'checkbox' ? (
                                                <label className="flex items-center gap-2 text-sm">
                                                    <input type="checkbox" disabled className="rounded border-stone-300" />
                                                    {field.placeholder || field.label}
                                                </label>
                                            ) : (
                                                <input disabled type={field.field_type === 'email' ? 'email' : field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : field.field_type === 'url' ? 'url' : field.field_type === 'phone' ? 'tel' : 'text'}
                                                    className={INPUT} placeholder={field.placeholder} />
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <button disabled className="mt-4 w-full py-2.5 rounded-lg text-white font-medium text-sm"
                                    style={{ backgroundColor: form.styling?.brand_color || '#1c1917' }}>
                                    {form.styling?.button_text || 'Submit'}
                                </button>
                                {form.styling?.show_branding !== false && (
                                    <p className="text-center text-[10px] text-stone-400 mt-3">Powered by NurtureCRM</p>
                                )}
                            </div>
                        )}

                        {activeTab === 'embed' && (
                            <div className="max-w-lg space-y-4">
                                {form.slug ? (
                                    <>
                                        <div>
                                            <label className={LABEL}>Public URL</label>
                                            <div className="flex items-center gap-2">
                                                <code className="flex-1 text-xs bg-stone-50 px-3 py-2 rounded border border-stone-200">
                                                    https://app.nurturecrm.ai/form/{form.slug}
                                                </code>
                                                <Button variant="outline" size="sm" onClick={() => {
                                                    navigator.clipboard.writeText(`https://app.nurturecrm.ai/form/${form.slug}`);
                                                    toast.success('URL copied');
                                                }}><Copy className="w-3.5 h-3.5" /></Button>
                                            </div>
                                        </div>
                                        <div>
                                            <label className={LABEL}>iframe Embed Code</label>
                                            <textarea readOnly rows={3} className="w-full text-xs font-mono bg-stone-50 px-3 py-2 rounded border border-stone-200"
                                                value={`<iframe src="https://app.nurturecrm.ai/form/${form.slug}?embed=true" width="100%" height="600" frameborder="0" style="border:none;max-width:640px;"></iframe>`} />
                                        </div>
                                        <div>
                                            <label className={LABEL}>Script Tag (auto-resize)</label>
                                            <textarea readOnly rows={5} className="w-full text-xs font-mono bg-stone-50 px-3 py-2 rounded border border-stone-200"
                                                value={`<div id="nurture-form-${form.slug}"></div>\n<script>\n(function(){var d=document.getElementById('nurture-form-${form.slug}');if(!d)return;var f=document.createElement('iframe');f.src='https://app.nurturecrm.ai/form/${form.slug}?embed=true';f.style.cssText='width:100%;border:none;max-width:640px;min-height:400px;';f.setAttribute('frameborder','0');d.appendChild(f);window.addEventListener('message',function(e){if(e.data&&e.data.type==='nurture-form-resize'&&e.data.slug==='${form.slug}'){f.style.height=e.data.height+'px';}});})();\n</script>`} />
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-sm text-stone-400">Save the form first to get embed codes.</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
